import { darkenToContrast } from "utils/accessibility";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  useTheme,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  EditOutlined as EditOutlinedIcon,
  DraftsOutlined as DraftsOutlinedIcon,
  CancelOutlined as CancelOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
} from "@mui/icons-material";
import CustomStepper from "components/CustomStepper";
import CustomDropdownButton from "global/components/CustomDropdownButton/CustomDropdownButton";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import SmsConsentDialog from "./components/SmsConsentDialog";
import CustomInput from "global/components/CustomInput/CustomInput";
import FolderTree from "screens/Templates/FolderTree";
import Unauthorized from "screens/Shared/Unauthorized";
import UploadDocuments from "./components/UploadDocuments";
import SignRequestEditor from "./components/SignRequestEditor";
import RequestConfiguration from "./components/RequestConfiguration";
import ApproversAndSigners from "./components/ApproversAndSigners";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectScreen,
  setChipLoader,
  setErrors,
  setIsRequest,
  setLoading,
  setReduxSelectedFile,
  setReduxSelectedParticipant,
  setReduxSignRequest,
  setUploadedFileNames,
  setUploadedFileNamesDelete,
  updateStatusBulkSend,
} from "redux/appSlice";
import {
  fetchDocumentDimensions,
  getExpirationDays,
  getIsAutoReminderConfig,
  getReminderConfig,
  recordSmsConsent,
  saveTemplate,
  sendBulkSignRequest,
  sendRequest,
  updateSignRequest,
} from "./SignRequestService";
import {
  addTemplate,
  createNewFolder,
  fetchFolderTree,
  updateTemplate,
} from "screens/Templates/MyTemplatesService";
import {
  CustomDialogComponent,
  ensureAttachmentPolicyForValidation,
  fieldColor,
  getContactSuggestion,
  handleDraftBtn,
  sendSignRequest,
  setIdOnEachField,
  validateField,
  validateAndNormalizeSignRequestFieldTypes,
} from "./SignRequestHelper";
import {
  addFile,
  convertFileForBackend,
  GetSaveTemplatePopups,
  initialState,
  setCoordinates,
  uploadCopiedFiles,
  uploadFiles,
} from "screens/Shared/CommonHelper";
import {
  fromPhoneParts,
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
  toPhoneParts,
} from "helpers/methods";
import {
  fillCopy,
  getSmsCreditCost,
} from "helpers/smsCreditHelpers";
import {
  anySignerRequiresIdv,
  handleIdvAwareError,
} from "helpers/identityVerificationHelpers";
import { apiStatus, getApiStatus } from "utils/apiStatus";
import useSmsCredits from "hooks/use-sms-credits";
import { hasAccessTo } from "layouts/Config";
import { useFeatureEnabled, useFeatureState } from "hooks/use-feature";
import { isEmbedEditorMode } from "embed/EmbedEditorMode";
import PresendReviewModal from "./components/PresendReview/PresendReviewModal";
import { usePresendReview } from "./components/PresendReview/usePresendReview";
import { buildPresendReviewPayload } from "./components/PresendReview/PresendReviewHelper";
import { PresendReviewFocus } from "./components/PresendReview/PresendReviewTypes";
import FeatureKeys from "global/constants/FeatureKeys";
import { useLocation, useParams } from "react-router-dom";
import {
  FileField,
  RequestFile,
  RequestParticipant,
  SignRequest,
  Reminders,
} from "models/interfaces";
import { RootState } from "utils/store";
import BulkSendParticipants from "./components/BulkSendParticipants";
import { is } from "immer/dist/internal";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import CustomSendingLoader from "global/components/CustomSendingLoader/CustomSendingLoader";
import DesktopAppPrompt from "./components/DesktopAppPrompt";
import { useTitle } from "utils/UseTitle";
import { useAriaLive } from "hooks/use-aria-live";
import { LiveRegion } from "components/live-region";
import title from "global/constants/TitleConstants";
import {
  getSignRequestData,
  getUnsignedDocumentUrl,
} from "screens/MySignRequests/MySignRequestService";
import {
  handleSignNow,
  resolveNeedReminders,
} from "screens/MySignRequests/MySignRequestHelper";
import CustomConfirmationModal from "global/components/ConfirmationModal/CustomConfirmationModal";
import MobileSignRequest from "./Mobile/MobileSignRequest";
import editorTokens from "./SignRequestEditor.tokens";
import { inkfree, notification } from "theme/colors";
import {
  CloseIcon as EditorCloseIcon,
  CheckIcon as EditorCheckIcon,
} from "./components/EditorIcons";

interface SignRequestComponentProps {
  useTemplate?: boolean;
  newTemplate?: boolean;
  updateTemplate?: boolean;
  editDraft?: boolean;
  copyRequest?: boolean;
  updateRequest?: boolean;
}

const SignRequestComponent: React.FC<SignRequestComponentProps> = (props) => {
  useTitle(title.SIGN_REQUEST);
  const { announcement, priority, announce } = useAriaLive();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const templateData: any = history.location.state;
  const {
    signRequest,
    isRequest,
    contacts,
    errors,
    loading,
    assignExistingFields,
    isBulkSend,
  } = useAppSelector((state: RootState) => state.app);
  const isMobile = useAppSelector(selectScreen);
  const delegationState = useFeatureState(FeatureKeys.ENVELOPE_DELEGATION);
  const isDelegationAvailable =
    delegationState.status === strings.FEATURE_STATUS_AVAILABLE;
  const reminderState = useFeatureState(FeatureKeys.REMINDER_AUTOMATIC);
  const isReminderAvailable =
    reminderState.status === strings.FEATURE_STATUS_AVAILABLE;
  const autoExpirationState = useFeatureState(
    FeatureKeys.ENVELOPE_AUTO_EXPIRATION,
  );
  const isAutoExpirationAvailable =
    autoExpirationState.status === strings.FEATURE_STATUS_AVAILABLE;
  const searchParams = new URLSearchParams(location.search);
  const initialStep = parseInt(searchParams.get("step") || "0", 10);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const sendEmailState = useFeatureState(FeatureKeys.ENVELOPE_SEND_EMAIL);
  const isSendEmailAvailable =
    sendEmailState.status === strings.FEATURE_STATUS_AVAILABLE;
  const sendEmailDisabledTooltip =
    sendEmailState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((sendEmailState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : sendEmailState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : undefined;
  /**
   * The account's prepaid SMS wallet. Fetched only for a real send — a
   * template is never dispatched, so it can't spend a credit.
   */
  const smsCredits = useSmsCredits(isRequest);
  // Pulled out because the hook's own object identity changes every render;
  // this callback is stable, and it is all `handleNext` needs from it.
  const refreshSmsCredits = smsCredits.refresh;
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isScheduleReq, setIsScheduleReq] = useState(false);
  const [smsConsentOpen, setSmsConsentOpen] = useState(false);
  const [smsConsentSubmitting, setSmsConsentSubmitting] = useState(false);
  /**
   * A bulk run costing more credits than the wallet holds. It is not refused
   * up front — the backend prices each generated request as it is dispatched,
   * so the run stops partway with the already-sent requests unaffected. The
   * sender gets to decide that before it starts.
   */
  const [bulkCreditWarning, setBulkCreditWarning] = useState(false);
  /**
   * The send-time credit refusal (402), kept on screen rather than only in a
   * toast: the message names both numbers and the customer needs a way to act
   * on it, and by the final step the Approvers & Signers banner that carries
   * the buy link is two steps behind them.
   */
  const [smsBlockedMessage, setSmsBlockedMessage] = useState<string>("");
  const [templateNamePopup, setTemplateNamePopup] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateNameError, setTemplateNameError] = useState("");
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [saveTemplatePopup, setSaveTemplatePopup] = useState(false);
  const [selectedFolderData, setSelectedFolderData] = useState<any>(null);
  const [folderTree, setFolderTree] = useState<any[]>([]);
  const [destFolder, setDestFolder] = useState<any>();
  const [showFolderTreePopup, setShowFolderTreePopup] = useState(false);
  const [signerToDelay, setSignerToDelay] = useState<
    Partial<RequestParticipant>
  >({
    id: -1,
    order: -1,
  });
  const [isDelaySigner, setIsDelaySigner] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [showSignDialog, setShowSignDialog] = useState(false);
  // Sending fans out documents + emails to every recipient, so it gets the
  // explanatory overlay instead of the bare `loading` spinner.
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [reqId, setReqId] = useState<string>("");
  const [isSigning, setIsSigning] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  /**
   * The pre-send AI review. Advisory: it opens over the editor when a new request's Next is clicked, and
   * its own Continue carries on to the next step. Hidden (Next goes straight on) when the AI assistant is off.
   */
  const isPresendReviewEnabled = useFeatureEnabled(FeatureKeys.AI_ASSISTANT);
  const presendReview = usePresendReview();
  const {
    open: openPresendReview,
    close: closePresendReview,
    authFailed: presendReviewAuthFailed,
    resetCache: resetPresendReviewCache,
  } = presendReview;
  // A document added, removed or uploaded again (a new upload gets a new link, even under the same name)
  // makes the last review stale although the request it answered may look the same: forget it.
  const presendReviewDocumentsKey = (signRequest.files || [])
    .map(
      (file: RequestFile) =>
        `${file.unsignedPdf || file.unsignedDoc}|${file.unsignedPdfUrl || file.unsignedDocUrl}`,
    )
    .join("\n");
  useEffect(() => {
    resetPresendReviewCache();
  }, [presendReviewDocumentsKey, resetPresendReviewCache]);
  /** Where a finding's "Show on page N" sends the editor once it has loaded. */
  const [reviewFocus, setReviewFocus] = useState<PresendReviewFocus | null>(
    null,
  );

  // Get current user email from auth
  const userEmail = useAppSelector((state: RootState) => state.auth.email);

  // Computed values
  // const searchParams = new URLSearchParams(location.search);
  const requestType = searchParams.get("type") || "others";
  const isBulkRequest = requestType === "bulkSend";

  /**
   * Credits this send will consume, counted the way the backend counts it —
   * one per signer receiving the document by text, plus one per signer
   * verifying with an SMS code. On a bulk run each recipient becomes its own
   * request, so the same total is what the whole run needs.
   */
  const smsCreditCost = useMemo(
    () => getSmsCreditCost(signRequest?.signers),
    [signRequest?.signers],
  );
  /**
   * The wallet can't cover this send. Only meaningful once the balance has
   * actually loaded: blocking Send on a failed or in-flight balance read would
   * stop a send the server would have accepted.
   */
  const isShortOfSmsCredits =
    isRequest &&
    smsCreditCost.total > 0 &&
    !smsCredits.isLoading &&
    !smsCredits.isUnavailable &&
    smsCredits.credits !== null &&
    smsCredits.balance < smsCreditCost.total;

  const hasMyRequestsFetchPermission = useMemo(
    () => hasAccessTo(strings.resources.sign, strings.fetchPermission),
    [],
  );

  const ALL_STEPS = [
    "Upload Document",
    isBulkRequest ? "Signers" : "Approvers and Signers",
    "Sign Request Editor",
    "Email configurations",
  ] as const;

  const STEP_CONFIGS = {
    signer: [0, 2, 3],
    others: [0, 1, 2, 3],
    bulkSend: [0, 1, 2, 3],
    default: [0, 1, 2, 3],
  } as const;

  /**
   * The send-blocked strip. Same amber-on-tint shape the Approvers & Signers
   * step uses for the credit banner, so the two read as the same warning at
   * either end of the flow — but red, because this send was actually refused.
   */
  const smsBlockedStripSx = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px",
    width: "100%",
    mt: 2,
    p: "10px 14px",
    borderRadius: "9px",
    bgcolor: notification.error.bg,
    border: `1px solid ${notification.error.border}`,
    boxSizing: "border-box",
  } as SxProps<Theme>;

  const smsBlockedTextSx = {
    flex: 1,
    minWidth: 0,
    fontSize: "12.5px",
    fontWeight: 600,
    color: notification.error.text,
    lineHeight: 1.45,
  } as SxProps<Theme>;

  const smsBlockedLinkSx = {
    flexShrink: 0,
    p: 0,
    border: 0,
    background: "none",
    fontSize: "12.5px",
    fontWeight: 700,
    color: inkfree.main,
    textDecoration: "underline",
    cursor: "pointer",
    whiteSpace: "nowrap",
  } as SxProps<Theme>;

  /**
   * The header's split primary action. `Sign Request Editor.dc.html` draws it
   * as a 38px pill whose caret is fused to the right of the label, so the
   * ButtonGroup's own radii and divider are overridden here.
   */
  const primaryActionSx = {
    "&.MuiButtonBase-root": {
      minWidth: "auto",
      minHeight: editorTokens.header.buttonHeight,
      height: editorTokens.header.buttonHeight,
      px: editorTokens.header.nextPaddingX,
      borderTopLeftRadius: editorTokens.header.buttonRadius,
      borderBottomLeftRadius: editorTokens.header.buttonRadius,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      textTransform: "none",
      fontSize: editorTokens.header.buttonFontSize,
      fontWeight: editorTokens.header.buttonFontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      boxShadow: "none",
      // `background` (not `backgroundColor`) — the theme's contained slot uses
      // the shorthand, which would otherwise reset a colour set here.
      background: editorTokens.palette.ctaBg,
      color: editorTokens.palette.white,
      borderColor: `${editorTokens.header.nextCaretDivider} !important`,
      "&:hover": {
        boxShadow: "none",
        background: editorTokens.palette.ctaBgHover,
      },
    },
  };

  const primaryActionCaretSx = {
    "&.MuiButtonBase-root": {
      minWidth: `${editorTokens.header.nextCaretWidth}px !important`,
      width: `${editorTokens.header.nextCaretWidth}px !important`,
      minHeight: `${editorTokens.header.buttonHeight}px !important`,
      height: `${editorTokens.header.buttonHeight}px !important`,
      padding: 0,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderTopRightRadius: editorTokens.header.buttonRadius,
      borderBottomRightRadius: editorTokens.header.buttonRadius,
      boxShadow: "none",
      background: editorTokens.palette.ctaBg,
      color: editorTokens.palette.white,
      "& .MuiSvgIcon-root": {
        fontSize: editorTokens.header.nextCaretIconSize,
      },
      "&:hover": {
        boxShadow: "none",
        background: editorTokens.palette.ctaBgHover,
      },
    },
  };

  /**
   * The "All changes saved" note only claims what is true: the flow has an
   * envelope persisted server-side (draft or existing request).
   */
  const hasSavedDraft = Boolean((signRequest as any)?.id);

  const steps = useMemo(() => {
    const stepIndices =
      STEP_CONFIGS[requestType as keyof typeof STEP_CONFIGS] ||
      STEP_CONFIGS.default;

    return stepIndices.map((stepIndex, index) => ({
      label: ALL_STEPS[stepIndex],
      isActive: currentStep === index,
      isCompleted: currentStep > index,
    }));
  }, [currentStep, requestType, isBulkRequest]);

  // Moving between steps swaps the whole panel without a route change, so
  // nothing announces it. Name the new step for screen readers; the first
  // render is skipped because `RouteAnnouncer` already reads the page title.
  const announcedStepRef = useRef<number | null>(null);
  useEffect(() => {
    const label = steps[currentStep]?.label;
    if (!label || announcedStepRef.current === currentStep) return;
    const isFirstRender = announcedStepRef.current === null;
    announcedStepRef.current = currentStep;
    if (isFirstRender) return;
    announce(`Step ${currentStep + 1} of ${steps.length}: ${label}`);
  }, [currentStep, steps, announce]);

  const isDataAvailable = () => {
    const isAllEmpty = (signRequest.signers || []).every(
      (signer: RequestParticipant) => signer.email === "" && signer.name === "",
    );
    if (
      !(props.useTemplate || props.updateTemplate || props.newTemplate) &&
      (isTruthy(signRequest.emailMessage) ||
        isTruthy(signRequest.emailSubject) ||
        isTruthy(signRequest.files) ||
        !isAllEmpty)
    ) {
      return true;
    } else {
      return false;
    }
  };

  const resetState = useCallback(() => {
    dispatch(setReduxSignRequest(null));
    dispatch(setReduxSelectedParticipant(null));
    dispatch(setReduxSelectedFile(null));
  }, [dispatch]);

  const fetchFolderTreeData = useCallback(async () => {
    try {
      const foldersTree = await fetchFolderTree();
      setFolderTree(foldersTree);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  }, []);

  const getAccountConfigs = useCallback(async () => {
    try {
      dispatch(setLoading(true));

      const [isAutoReminder, expirationDays] = await Promise.all([
        getIsAutoReminderConfig(),
        getExpirationDays(),
      ]);

      dispatch(setReduxSignRequest({ needReminders: isAutoReminder }));

      const reminder: Reminders = isAutoReminder
        ? await getReminderConfig()
        : signRequest.reminders;

      const updates = {
        reminders:
          isTruthy(reminder.daysBeforeFirstReminder) &&
          isTruthy(reminder.daysBetweenReminders)
            ? reminder
            : signRequest.reminders,
        expirationDays: isTruthy(expirationDays)
          ? expirationDays
          : signRequest.expirationDays,
      };

      dispatch(setReduxSignRequest(updates));
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, signRequest.reminders, signRequest.expirationDays]);

  const processTemplateData = (signRequestFromTemp: SignRequest) => {
    if (!signRequestFromTemp.approval) {
      signRequestFromTemp.approval = {
        approvalNeeded: false,
        orderNeeded: false,
        reviewers: [],
      };
    }

    if (signRequestFromTemp.approval.reviewers) {
      signRequestFromTemp.approval.reviewers =
        signRequestFromTemp.approval.reviewers.map((reviewer) => ({
          ...reviewer,
          color: reviewer.color
            ? darkenToContrast(reviewer.color)
            : fieldColor[reviewer.id],
        }));
    }
    if (signRequestFromTemp.files) {
      signRequestFromTemp.files = signRequestFromTemp.files.map(
        (file: any) => ({
          ...file,
          unsignedDoc: file.unsignedPdf || file.unsignedDoc,
          unsignedDocUrl: file.unsignedPdfUrl || file.unsignedDocUrl,
          isUploading: props.copyRequest ? true : file.isUploading || false,
        }),
      );
    }
    signRequestFromTemp.needReminders = resolveNeedReminders(signRequestFromTemp);
    if (signRequestFromTemp.signRequestConfig === null) {
      signRequestFromTemp.signRequestConfig = initialState.signRequestConfig;
    }
    // When updating an existing request that had a signer delegate to someone
    // else, drop the original (now-delegated) signer so the update form only
    // shows the actual current participants. Reset the `delegated` flag on the
    // remaining signers so the update payload doesn't carry stale delegation
    // state. Matches the behavior of copySignRequest.
    const sourceSigners =
      props.updateRequest && Array.isArray(signRequestFromTemp?.signers)
        ? signRequestFromTemp.signers
            .filter(
              (signer: RequestParticipant) =>
                signer.status !== strings.delegated,
            )
            .map((signer: RequestParticipant) => ({
              ...signer,
              delegated: false,
            }))
        : signRequestFromTemp?.signers || [];

    // When using a template, any per-envelope verification state on the
    // source (security code, pending OTP, verification result, failed-attempt
    // counter) must not be carried into the new request. Only the security
    // mode the requester picked (needSecuredAccess / needOTPValidation) is
    // kept so the dropdown selection persists. Matches copySignRequest.
    const shouldScrubSecurityCode = !!props.useTemplate;
    const scrubSecurity = (p: RequestParticipant): RequestParticipant =>
      shouldScrubSecurityCode && p.requestSecurity
        ? {
            ...p,
            requestSecurity: {
              ...p.requestSecurity,
              securityCode: null,
              otp: null,
              otpValidUntil: null,
              isVerified: false,
              verified: false,
              verifiedAt: null,
              failedAttempts: 0,
              // Server-redacted (e.g. "••••4567"), same as the other
              // per-envelope verification state above — stale once reused.
              authPhoneHint: null,
            },
          }
        : p;

    const signersWithColor = sourceSigners.map(
      (signer: RequestParticipant) => {
        const scrubbed = scrubSecurity(signer);
        // Hydrate the backend's split { countryCode, number } phone objects back
        // into the display string CustomContactNumberInput expects. A string
        // (never-sent) passes through unchanged.
        return {
          ...scrubbed,
          phone: fromPhoneParts(scrubbed.phone as any),
          ...(scrubbed.requestSecurity
            ? {
                requestSecurity: {
                  ...scrubbed.requestSecurity,
                  authPhone: fromPhoneParts(
                    scrubbed.requestSecurity.authPhone as any,
                  ),
                },
              }
            : {}),
          color: fieldColor[signer.id],
        };
      },
    );
    signRequestFromTemp.signers = signersWithColor;

    if (shouldScrubSecurityCode && signRequestFromTemp.approval?.reviewers) {
      signRequestFromTemp.approval.reviewers =
        signRequestFromTemp.approval.reviewers.map(scrubSecurity);
    }

    if (props.useTemplate) {
      const tid = signRequestFromTemp.id;
      signRequestFromTemp.templateId =
        tid != null && String(tid).trim() !== "" ? String(tid) : null;
      // The source template's parentId belongs to the template folder tree and
      // is meaningless for a sign request. Requests created from a template
      // always start at the sign-request root.
      signRequestFromTemp.parentId = 0;
      delete (signRequestFromTemp as SignRequest & { path?: string }).path;
    } else if (props.copyRequest || props.updateTemplate) {
      signRequestFromTemp.templateId = null;
    } else {
      signRequestFromTemp.templateId = signRequestFromTemp.templateId ?? null;
    }

    return signRequestFromTemp;
  };

  const handleNext = useCallback(
    async (updateRequest: boolean = false, skipReview: boolean = false) => {
      try {
        setSmsBlockedMessage("");
        if (steps[currentStep]?.label === "Sign Request Editor") {
          await ensureAttachmentPolicyForValidation(signRequest);
        }
        const { isValid: isCurrentStepValid, firstErrorMessage } =
          validateField(
            signRequest,
            errors,
            dispatch,
            props,
            steps[currentStep]?.label,
            isBulkSend,
            assignExistingFields,
            (props.newTemplate || props.updateTemplate)!,
          );

        if (!isCurrentStepValid) {
          if (firstErrorMessage) {
            openErrorNotification(firstErrorMessage);
          }
          return;
        }

        if (currentStep === steps.length - 1) {
          // Validate and normalize field types before sending/updating request
          // This will automatically fix types like "Yvette-date_signed-1" to "date_signed"
          const validationResult =
            validateAndNormalizeSignRequestFieldTypes(signRequest);

          // Always use the normalized request (with corrected field types)
          const requestObject = setCoordinates(
            validationResult.normalizedRequest,
          );

          if (!isDelegationAvailable && requestObject?.signRequestConfig) {
            requestObject.signRequestConfig = {
              ...requestObject.signRequestConfig,
              allowSignatureDelegation: false,
            };
          }

          if (!isReminderAvailable) {
            requestObject.needReminders = false;
            requestObject.reminders = null;
          }

          if (!isAutoExpirationAvailable) {
            delete (requestObject as any).expirationDays;
          }

          if (isBulkSend) {
            // A bulk run is priced per generated request as each is
            // dispatched, not for the whole run up front, so it can stop
            // partway. Confirm before starting one the wallet can't finish.
            if (isShortOfSmsCredits && !bulkCreditWarning) {
              setBulkCreditWarning(true);
              return;
            }
            setBulkCreditWarning(false);
            dispatch(setLoading(true));
            sendBulkSignRequest(requestObject);
            dispatch(setLoading(false));
            dispatch(setReduxSignRequest(null));
            openSuccessNotification("Requests sent successfully!");
            history.push({
              pathname: viewpaths.sentSignRequestsViewPath,
              state: { requestType: strings.sentRequest },
            });
          } else {
            if (isRequest) {
              try {
                setIsSendingRequest(true);
                dispatch(setLoading(true));
                if (updateRequest) {
                  await updateSignRequest(requestObject);
                  dispatch(setReduxSignRequest(null));
                  openSuccessNotification("Request updated successfully!");
                  history.push({
                    pathname: viewpaths.sentSignRequestsViewPath,
                    state: { requestType: strings.sentRequest },
                  });
                } else {
                  const response = await sendRequest(requestObject);
                  openSuccessNotification("Request sent successfully!");

                  if (requestType === "signer") {
                    setReqId(response.message);
                    dispatch(setLoading(false));
                    setShowSignDialog(true);
                    return;
                  }

                  dispatch(setLoading(false));
                  history.push({
                    pathname: viewpaths.sentSignRequestsViewPath,
                    state: { requestType: strings.sentRequest },
                  });
                  dispatch(setReduxSignRequest(null));
                }
              } finally {
                setIsSendingRequest(false);
                dispatch(setLoading(false));
              }
            } else {
              setTemplateName(
                isTruthy(signRequest.templateName)
                  ? signRequest.templateName
                  : "",
              );
              setTemplateNamePopup(true);
            }
          }
        } else if (currentStep < steps.length - 1) {
          const label = steps[currentStep]?.label;
          // Leaving the editor of a new request opens the pre-send review over
          // it. It never blocks: its Continue comes back here with `skipReview`.
          if (
            label === "Sign Request Editor" &&
            isRequest &&
            !updateRequest &&
            !isBulkSend &&
            !skipReview &&
            isPresendReviewEnabled &&
            !presendReviewAuthFailed &&
            !isEmbedEditorMode() &&
            isTruthy(signRequest.id)
          ) {
            // Reviewed exactly as it will be sent: field types normalised and
            // boxes converted to page coordinates, as the final Send does. A
            // request that cannot be converted yet (a page size still loading)
            // skips the review rather than holding Next up.
            let reviewPayload: ReturnType<
              typeof buildPresendReviewPayload
            > | null = null;
            try {
              reviewPayload = buildPresendReviewPayload(
                setCoordinates(
                  validateAndNormalizeSignRequestFieldTypes(signRequest)
                    .normalizedRequest,
                ),
              );
            } catch {
              reviewPayload = null;
            }
            if (reviewPayload) {
              setReviewFocus(null);
              openPresendReview(signRequest.id, reviewPayload);
              return;
            }
          }
          // Leaving the signers step with any SMS recipient needs the sender to
          // confirm the recipient has agreed to be texted (SMS consent).
          const isSignersStep =
            label === "Approvers and Signers" || label === "Signers";
          const anySignerUsesSms = (signRequest?.signers ?? []).some(
            (signer: RequestParticipant) =>
              (signer.deliveryChannels ?? []).includes(
                strings.deliveryChannels.sms as any,
              ),
          );
          if (isSignersStep && anySignerUsesSms) {
            setSmsConsentOpen(true);
            return;
          }
          setCurrentStep(currentStep + 1);
        }
      } catch (error: any) {
        // The send-time credit check refuses the whole request with 402 and a
        // message naming both numbers ("needs 5 … you have 4"), which reads
        // better than anything built here — show it as-is and re-read the
        // balance, which has just been proven stale. The buy route sits on the
        // banner one step back rather than inside a toast.
        if (
          getApiStatus(error) === apiStatus.paymentRequired &&
          typeof error?.message === "string"
        ) {
          // The balance has just been proven stale, so re-read it; the message
          // is written to be shown as-is and goes in the strip below the
          // header, which carries the buy link a toast cannot.
          refreshSmsCredits();
          setSmsBlockedMessage(error.message);
          return;
        }
        handleIdvAwareError(error, anySignerRequiresIdv(signRequest?.signers));
      }
    },
    [
      signRequest,
      errors,
      dispatch,
      props,
      currentStep,
      steps.length,
      isRequest,
      isDelegationAvailable,
      isReminderAvailable,
      isAutoExpirationAvailable,
      isShortOfSmsCredits,
      bulkCreditWarning,
      refreshSmsCredits,
      isPresendReviewEnabled,
      presendReviewAuthFailed,
      openPresendReview,
      isBulkSend,
    ],
  );

  /** The review's Continue / Continue anyway: the step change the review interrupted. */
  const handlePresendReviewContinue = useCallback(() => {
    closePresendReview();
    handleNext(props.updateRequest, true);
  }, [closePresendReview, handleNext, props.updateRequest]);

  /**
   * "Show on page N": the popup sits over the editor, so it only closes; the editor opens the finding's
   * document, goes to the page and selects the field.
   */
  const handlePresendReviewShowOnPage = useCallback(
    (focus: PresendReviewFocus) => {
      closePresendReview();
      setReviewFocus(focus);
    },
    [closePresendReview],
  );

  const changeStep = useCallback(
    async (nextStep: number) => {
      let isNextStepValid = true;
      let firstErrorMessage: string | undefined;

      if (currentStep < nextStep) {
        for (let i = currentStep; i < nextStep; i++) {
          if (steps[i]?.label === "Sign Request Editor") {
            await ensureAttachmentPolicyForValidation(signRequest);
          }
          const result = validateField(
            signRequest,
            errors,
            dispatch,
            props,
            steps[i]?.label,
            isBulkSend,
            assignExistingFields,
            (props.newTemplate || props.updateTemplate)!,
          );
          isNextStepValid = result.isValid;
          if (!result.isValid && result.firstErrorMessage) {
            firstErrorMessage = result.firstErrorMessage;
            break;
          }
        }
      }
      if (isNextStepValid) {
        setCurrentStep(nextStep);
      } else if (firstErrorMessage) {
        openErrorNotification(firstErrorMessage);
      }
    },
    [
      signRequest,
      errors,
      dispatch,
      props,
      currentStep,
      steps.length,
      isRequest,
    ],
  );

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      dispatch(setErrors({}));
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, dispatch]);

  /**
   * SMS consent confirmed — record the sender's attestation server-side (SMS
   * delivery is rejected without it) before advancing. Blocks the step change
   * if recording fails so the request can never be sent without consent on file.
   */
  const handleSmsConsentConfirm = useCallback(async () => {
    const phoneNumbers: string[] = Array.from(
      new Set<string>(
        (signRequest?.signers ?? [])
          .filter(
            (signer: RequestParticipant) =>
              !signer.cc &&
              signer.deliveryMode !== strings.deliveryModes.inPerson &&
              (signer.deliveryChannels ?? []).includes(
                strings.deliveryChannels.sms as any,
              ),
          )
          .map((signer: RequestParticipant): string => {
            const parts = toPhoneParts(signer.phone);
            return parts ? `${parts.countryCode}${parts.number}` : "";
          })
          .filter((num: string) => Boolean(num)),
      ),
    );

    try {
      setSmsConsentSubmitting(true);
      await recordSmsConsent({
        consentReferenceId: String(signRequest?.id ?? ""),
        phoneNumbers,
        confirmed: true,
      });
      setSmsConsentOpen(false);
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    } catch (error: any) {
      // Every response from this endpoint — success or error — carries
      // `{ code, message }` with a message that is already user-facing text
      // (validation reasons, or the "no SMS consent agreement configured"
      // system error), so show it directly rather than the generic
      // by-status fallback in `handleErrorMessage`.
      openErrorNotification(
        typeof error?.message === "string"
          ? error.message
          : "There was an error on the server side. Please try again later.",
      );
    } finally {
      setSmsConsentSubmitting(false);
    }
  }, [signRequest?.signers, signRequest?.id, currentStep, steps.length]);

  const handleCancel = useCallback(() => {
    resetState();

    const urlParts = window.location.pathname.split("/");
    const lastWord =
      urlParts[urlParts.length - 1] || urlParts[urlParts.length - 2];

    if (lastWord === "request") {
      history.push(viewpaths.sentSignRequestsViewPath);
    } else if (lastWord === "template") {
      history.push(viewpaths.templatesViewPath);
    } else if (lastWord === "draft") {
      history.push(viewpaths.sentSignRequestsViewPath);
    } else {
      history.goBack();
    }
  }, [resetState, history]);

  const handleDialogClose = useCallback(() => {
    setTemplateNamePopup(false);
    setTemplateNameError("");
    setTemplateName("");
  }, []);

  const handleTemplate = useCallback(async () => {
    // Validate and normalize field types before saving template
    // This will automatically fix types like "Yvette-date_signed-1" to "date_signed"
    const validationResult =
      validateAndNormalizeSignRequestFieldTypes(signRequest);

    // Always use the normalized request (with corrected field types)
    const requestObject = setCoordinates(validationResult.normalizedRequest);
    requestObject.type = strings.template;
    requestObject.templateName = templateName;

    try {
      dispatch(setLoading(true));
      await (props.updateTemplate
        ? updateTemplate(requestObject, false)
        : addTemplate(requestObject));
      setTemplateNamePopup(false);
      openSuccessNotification("Template saved successfully!");

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("fromWebForm") === "true") {
        const channel = new BroadcastChannel("template_updates");
        channel.postMessage({
          type: "TEMPLATE_UPDATED",
          templateId: requestObject.id,
        });
        channel.close();

        localStorage.setItem(
          `webform_template_${requestObject.id}`,
          JSON.stringify(requestObject),
        );

        window.close();
      } else {
        history.push(viewpaths.templatesViewPath);
      }
    } catch (error: any) {
      // Surface backend failures (e.g. 406 "The template <name> already
      // exists"). Without this the error was swallowed by the bare try/finally
      // and no notification showed. The name popup stays open so the user can
      // pick a different name and retry.
      handleIdvAwareError(error, anySignerRequiresIdv(requestObject?.signers));
    } finally {
      dispatch(setLoading(false));
    }
  }, [signRequest, templateName, dispatch, props.updateTemplate]);

  const handleSign = async () => {
    try {
      setIsSigning(true);
      const requestResponse = await getSignRequestData(reqId);
      const signerUrl = requestResponse?.signers[0].url;
      if (signerUrl) {
        window.open(signerUrl, "_blank");
        setShowSignDialog(false);
        setReqId("");
        history.push({
          pathname: viewpaths.sentSignRequestsViewPath,
          state: { requestType: strings.sentRequest },
        });
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsSigning(false);
    }
  };

  const handleSkipSign = useCallback(() => {
    setShowSignDialog(false);
    history.push({
      pathname: viewpaths.sentSignRequestsViewPath,
      state: { requestType: strings.sentRequest },
    });
  }, []);

  const handleSaveAsDraft = useCallback(async () => {
    handleDraftBtn(signRequest, setSaveAsDraft, dispatch);
    setSaveAsDraft(false);
  }, [isDataAvailable, signRequest, dispatch]);

  // Reset stale request state on mount so re-entering the flow after a direct
  // URL change in the address bar starts with an empty form. Template/draft data
  // is loaded separately from history.location.state by the effect below.
  useEffect(() => {
    resetState();
    const stateParentId = (history.location.state as any)?.parentId;
    if (stateParentId != null) {
      dispatch(setReduxSignRequest({ parentId: stateParentId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !signRequest?.files?.length ||
      props.updateTemplate ||
      props.updateRequest ||
      props.newTemplate
    )
      return;

    const handleBackButton = (event: PopStateEvent) => {
      event.preventDefault();

      setOpenConfirm(true);

      // keep user on same page
      window.history.pushState(null, "", window.location.href);
    };

    // add fake history entry ONLY when files exist
    window.history.pushState(null, "", window.location.href);

    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [signRequest?.files]);

  useEffect(() => {
    dispatch(setErrors({}));
  }, [currentStep, dispatch]);

  useEffect(() => {
    if (isBulkSend !== isBulkRequest) {
      dispatch(updateStatusBulkSend(isBulkRequest));
    }
  }, [isBulkRequest, isBulkSend, dispatch]);

  useEffect(() => {
    if (isBulkSend !== isBulkRequest) {
      dispatch(updateStatusBulkSend(isBulkRequest));
    }
  }, [isBulkRequest, isBulkSend, dispatch]);

  useEffect(() => {
    if (isBulkSend !== isBulkRequest) {
      dispatch(updateStatusBulkSend(isBulkRequest));
    }
  }, [isBulkRequest, isBulkSend, dispatch]);

  useEffect(() => {
    const loadTemplateData = async () => {
      let dataToProcess = templateData;

      if (!dataToProcess && id) {
        try {
          dispatch(setLoading(true));
          const localTemplateStr = localStorage.getItem(
            `webform_template_${id}`,
          );
          if (localTemplateStr) {
            dataToProcess = JSON.parse(localTemplateStr);
          }

          if (dataToProcess?.files && dataToProcess.files.length > 0) {
            const filesWithUrls = await Promise.all(
              dataToProcess.files.map(async (file: any) => {
                try {
                  const [res, dimRes] = await Promise.all([
                    getUnsignedDocumentUrl(file, false, id),
                    fetchDocumentDimensions(
                      { unsignedPdf: file.unsignedPdf || file.unsignedDoc },
                      false,
                      id,
                      false,
                    ).catch(() => undefined),
                  ]);
                  return {
                    ...file,
                    unsignedPdfUrl:
                      res?.message ||
                      file.unsignedPdfUrl ||
                      file.unsignedDocUrl,
                    unsignedDocUrl:
                      res?.message ||
                      file.unsignedDocUrl ||
                      file.unsignedPdfUrl,
                    dimensions: dimRes || file.dimensions || {},
                  };
                } catch (e) {
                  return file;
                }
              }),
            );
            dataToProcess = { ...dataToProcess, files: filesWithUrls };
          }
        } catch (error: any) {
          handleErrorMessage(error);
          dataToProcess = null;
        } finally {
          dispatch(setLoading(false));
        }
      }

      if (!dataToProcess) {
        getAccountConfigs();
        return;
      }

      const signRequestFromTemp = structuredClone({
        ...dataToProcess,
        approval: dataToProcess?.approval || {
          approvalNeeded: false,
          orderNeeded: false,
          reviewers: [],
        },
      }) as SignRequest;

      const processedData = processTemplateData(signRequestFromTemp);
      const envelopeId = processedData?.id;

      if (Array.isArray(processedData.files)) {
        processedData.files = processedData.files.map((file: any) => ({
          ...file,
          unsignedDoc: file.unsignedDoc || file.unsignedPdf || "",
          unsignedDocUrl: file.unsignedDocUrl || file.unsignedPdfUrl || "",
        }));
      } else {
        processedData.files = [];
      }

      setIdOnEachField(processedData.files, props);
      dispatch(setReduxSignRequest(processedData));

      if (props.useTemplate || props.copyRequest) {
        if (props.copyRequest) {
          dispatch(setChipLoader(true));
        }
        uploadFiles(
          processedData,
          setIsFileUploaded,
          !props.useTemplate,
          true,
          dispatch,
          true,
        );
      } else if (
        props.updateTemplate ||
        props.editDraft ||
        props.updateRequest
      ) {
        for (const file of processedData.files) {
          dispatch(setUploadedFileNames([file.unsignedDoc]));
          addFile(file, isRequest, dispatch, envelopeId, false);
        }
      }
    };

    loadTemplateData();
  }, [templateData, id, props, dispatch, isRequest]);

  useEffect(() => {
    fetchFolderTreeData();
  }, [fetchFolderTreeData]);

  useEffect(() => {
    const isAccess = hasAccessTo(
      strings.resources.contacts,
      strings.fetchPermission,
    );
    if (contacts.length < 10 && isAccess) {
      getContactSuggestion("", dispatch);
    }
  }, [contacts.length, dispatch]);

  useEffect(() => {
    if (props.newTemplate || props.updateTemplate) {
      dispatch(setIsRequest(false));
    }
  }, [props.newTemplate, props.updateTemplate, dispatch]);

  const isEditorStep = steps[currentStep]?.label === "Sign Request Editor";
  /**
   * The re-skinned steps own their top padding (their designs set it), so the
   * step wrapper must not add its own above them.
   */
  const stepOwnsTopPadding =
    isEditorStep ||
    steps[currentStep]?.label === "Email configurations" ||
    steps[currentStep]?.label === "Signers" ||
    (isBulkRequest && steps[currentStep]?.label === "Upload Document");

  const getStepContent = () => {
    const currentStepLabel = steps[currentStep]?.label;

    switch (currentStepLabel) {
      case "Upload Document":
        return (
          <UploadDocuments
            updateTemplate={!!templateData?.id}
            isNewTemplate={templateData?.newTemplate}
            bulk
          />
        );
      case "Approvers and Signers":
        return (
          <ApproversAndSigners
            smsCreditBalance={smsCredits.balance}
            smsCreditCost={smsCreditCost.total}
            smsCreditMaxCost={smsCreditCost.maxTotal}
            showSmsCreditBanner={
              isRequest &&
              !smsCredits.isLoading &&
              !smsCredits.isUnavailable &&
              smsCredits.credits !== null
            }
          />
        );
      case "Signers":
        return <BulkSendParticipants />;
      case "Sign Request Editor":
        return (
          <SignRequestEditor
            setLoader={setLoader}
            onManageParticipants={handlePrevious}
            onAddDocuments={() => changeStep(0)}
            reviewFocus={reviewFocus}
            onReviewFocusHandled={() => setReviewFocus(null)}
          />
        );
      case "Email configurations":
        return (
          <RequestConfiguration
            openDatePicker={openDatePicker}
            setOpenDatePicker={setOpenDatePicker}
            setIsScheduleRequest={setIsScheduleReq}
            isScheduleReq={isScheduleReq}
            signerToDelay={signerToDelay}
            setIsDelaySigner={setIsDelaySigner}
            setLoader={setLoader}
          />
        );
      default:
        return null;
    }
  };

  const isSignAccess = hasAccessTo(
    strings.resources.sign,
    strings.sendPermission,
  );
  const isTemplateAddAccess = hasAccessTo(
    strings.resources.template,
    strings.addPermission,
  );
  const isTemplateUpdateAccess = hasAccessTo(
    strings.resources.template,
    strings.updatePermission,
  );

  const templateUseState = useFeatureState(FeatureKeys.TEMPLATE_USE);
  const isTemplateFeatureAvailable =
    templateUseState.status === strings.FEATURE_STATUS_AVAILABLE;
  const templateDisabledTooltip =
    templateUseState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((templateUseState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : templateUseState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : undefined;

  const dropdownMenuItems = useMemo(
    () => [
      ...(["Sign Request Editor", "Email configurations"].includes(
        steps[currentStep]?.label,
      ) &&
      isRequest &&
      isTemplateAddAccess
        ? [
            {
              icon: (
                <EditOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: isTemplateFeatureAvailable
                      ? theme.palette.primary.main
                      : theme.palette.action.disabled,
                  }}
                />
              ),
              label: "Save as Template",
              onClick: isTemplateFeatureAvailable
                ? () => setSaveTemplatePopup(true)
                : () => {},
              disabled: !isTemplateFeatureAvailable,
              endIcon: !isTemplateFeatureAvailable ? (
                <LockOutlinedIcon
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
              ) : undefined,
              tooltip: !isTemplateFeatureAvailable
                ? templateDisabledTooltip
                : undefined,
            },
          ]
        : []),
      ...(isRequest && isSignAccess
        ? [
            {
              icon: (
                <DraftsOutlinedIcon
                  sx={{ fontSize: 17, color: theme.palette.primary.main }}
                />
              ),
              label: "Save as Draft",
              onClick: () => setSaveAsDraft(true),
            },
          ]
        : []),
    ],
    [
      steps,
      currentStep,
      theme.palette.primary.main,
      handleCancel,
      isRequest,
      isTemplateAddAccess,
      isSignAccess,
      isTemplateFeatureAvailable,
      templateDisabledTooltip,
    ],
  );

  if (!hasMyRequestsFetchPermission && isRequest) {
    return <Unauthorized permissionDenied={hasMyRequestsFetchPermission} />;
  }

  const isButtonDisabled = () => {
    return (
      signRequest.files.some((file: RequestFile) => file.isUploading) ||
      loading ||
      loader
    );
  };

  return (
    <>
      <CustomLoader isLoading={loading && !isSendingRequest} />
      <CustomSendingLoader
        isLoading={isSendingRequest}
        title={
          props.updateRequest
            ? strings.UPDATING_REQUEST_TITLE
            : strings.SENDING_REQUEST_TITLE
        }
        description={
          props.updateRequest
            ? strings.UPDATING_REQUEST_DESCRIPTION
            : strings.SENDING_REQUEST_DESCRIPTION
        }
      />
      {isMobile ? (
        <MobileSignRequest
          flowProps={props}
          steps={steps}
          currentStep={currentStep}
          changeStep={changeStep}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          onBack={() => {
            if (
              !signRequest?.files?.length ||
              props.updateTemplate ||
              props.updateRequest ||
              props.newTemplate
            ) {
              handleCancel();
              return;
            }
            setOpenConfirm(true);
          }}
          menuItems={dropdownMenuItems}
          continueLabel={
            currentStep === steps.length - 1
              ? isRequest
                ? props.updateRequest
                  ? strings.update
                  : strings.send
                : props.updateTemplate
                  ? strings.update
                  : strings.save
              : "Continue"
          }
          // Same credit block the desktop Send carries: out of credits, the
          // server refuses the whole request, so stop it here rather than
          // after a round trip. Bulk is excluded — that run is confirmed
          // rather than blocked.
          isContinueDisabled={
            isButtonDisabled() ||
            (currentStep === steps.length - 1 &&
              isRequest &&
              !isBulkSend &&
              isShortOfSmsCredits)
          }
          smsCreditBalance={smsCredits.balance}
          smsCreditCost={smsCreditCost.total}
          smsCreditMaxCost={smsCreditCost.maxTotal}
          showSmsCreditBanner={
            isRequest &&
            !smsCredits.isLoading &&
            !smsCredits.isUnavailable &&
            smsCredits.credits !== null
          }
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            minHeight: {
              xs: "100vh",
              md: isEditorStep ? "auto" : "100vh",
            },
          }}
        >
          <LiveRegion message={announcement} priority={priority} />
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              height: editorTokens.header.height,
              flexShrink: 0,
              bgcolor: editorTokens.header.bg,
              borderBottom: `1px solid ${editorTokens.header.borderColor}`,
              display: "flex",
              alignItems: "center",
              px: editorTokens.header.paddingX,
              gap: editorTokens.header.gap,
            }}
          >
            <Tooltip title="Cancel" arrow>
              <IconButton
                onClick={() => {
                  if (
                    !signRequest?.files?.length ||
                    props.updateTemplate ||
                    props.updateRequest ||
                    props.newTemplate
                  ) {
                    handleCancel();
                    return;
                  }

                  setOpenConfirm(true);
                }}
                sx={{
                  p: 0,
                  flexShrink: 0,
                  width: editorTokens.header.closeSize,
                  height: editorTokens.header.closeSize,
                  borderRadius: editorTokens.header.closeRadius,
                  color: editorTokens.header.closeColor,
                  "&:hover": { bgcolor: editorTokens.header.hoverBg },
                }}
              >
                <EditorCloseIcon size={editorTokens.header.closeIconSize} />
              </IconButton>
            </Tooltip>

            {isBulkRequest && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  flexShrink: 0,
                  px: "9px",
                  py: "5px",
                  borderRadius: "999px",
                  backgroundColor: editorTokens.palette.accentTint,
                  border: `1px solid ${editorTokens.palette.accentBorder}`,
                  boxSizing: "border-box",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={editorTokens.palette.accent}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: 14, height: 14, flexShrink: 0 }}
                >
                  <path d="M22 2L11 13" />
                  <path d="M22 2l-7 20-4-9-9-4z" />
                </svg>
                <Box
                  component="span"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 800,
                    color: "signer.orange.dark", // accent is ~4:1 on the pill tint (1.4.3)
                    letterSpacing: ".03em",
                    whiteSpace: "nowrap",
                  }}
                >
                  BULK SEND
                </Box>
              </Box>
            )}

            <CustomStepper
              steps={steps}
              currentStep={currentStep}
              handleChangeStep={changeStep}
            />

            {/* {hasSavedDraft && (
              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: editorTokens.header.savedGap,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    color: editorTokens.header.savedIconColor,
                  }}
                >
                  <EditorCheckIcon size={editorTokens.header.savedIconSize} />
                </Box>
                <Typography
                  component="span"
                  sx={{
                    fontSize: editorTokens.header.savedFontSize,
                    color: editorTokens.header.savedColor,
                    lineHeight: "normal",
                    whiteSpace: "nowrap",
                  }}
                >
                  All changes saved
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                flexShrink: 0,
                width: editorTokens.header.dividerWidth,
                height: editorTokens.header.dividerHeight,
                bgcolor: editorTokens.header.borderColor,
              }}
            /> */}

            {currentStep > 0 && (
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={isButtonDisabled()}
                sx={{
                  "&.MuiButton-root": {
                    flexShrink: 0,
                    height: editorTokens.header.buttonHeight,
                    minWidth: "auto",
                    px: editorTokens.header.previousPaddingX,
                    py: 0,
                    borderRadius: editorTokens.header.buttonRadius,
                    // Theme's `outlined` slot writes the `border` and
                    // `background` shorthands, so restate both here.
                    border: `${editorTokens.header.previousBorderWidth} solid ${editorTokens.palette.accent}`,
                    background: editorTokens.palette.white,
                    color: editorTokens.palette.accent,
                    textTransform: "none",
                    fontSize: editorTokens.header.buttonFontSize,
                    fontWeight: editorTokens.header.buttonFontWeight,
                    letterSpacing: "normal",
                    lineHeight: "normal",
                    "&:hover": {
                      border: `${editorTokens.header.previousBorderWidth} solid ${editorTokens.palette.accent}`,
                      background: editorTokens.header.hoverBg,
                    },
                  },
                }}
              >
                Previous
              </Button>
            )}

            <Box sx={{ flexShrink: 0, display: "flex" }}>
              {(() => {
                const isFinalSendStep =
                  currentStep === steps.length - 1 && isRequest;
                const isSendGated = isFinalSendStep && !isSendEmailAvailable;
                // Out of credits blocks the send server-side anyway; catching
                // it here saves the sender a round trip. Bulk is excluded —
                // that run is confirmed rather than blocked, since the
                // requests it can afford still go out.
                const isSendCreditBlocked =
                  isFinalSendStep && !isBulkSend && isShortOfSmsCredits;
                const actionLabel =
                  currentStep === steps.length - 1
                    ? isRequest
                      ? props.updateRequest
                        ? strings.update
                        : isTruthy(signRequest.scheduleTime)
                          ? strings.scheduleRequest
                          : requestType === "signer"
                            ? strings.sign
                            : strings.send
                      : props.updateTemplate
                        ? strings.update
                        : strings.save
                    : strings.next;
                const isDisabled =
                  isButtonDisabled() ||
                  !signRequest.files ||
                  signRequest.files.length === 0;
                // These only block sending — Save as Draft / Template in the
                // caret menu don't send, so they stay reachable.
                const isSendBlocked = isSendGated || isSendCreditBlocked;
                const startIcon = isSendGated ? (
                  <LockOutlinedIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                ) : undefined;
                // Template flows have no dropdown options, so the split button
                // has nothing to open — render a plain button rounded on all
                // four corners instead of the split button's left-only half.
                const actionButton =
                  dropdownMenuItems.length === 0 ? (
                    <Button
                      variant="contained"
                      onClick={() => handleNext(props.updateRequest)}
                      startIcon={startIcon}
                      disabled={isDisabled || isSendBlocked}
                      sx={{
                        "&.MuiButtonBase-root": {
                          ...primaryActionSx["&.MuiButtonBase-root"],
                          borderTopRightRadius:
                            editorTokens.header.buttonRadius,
                          borderBottomRightRadius:
                            editorTokens.header.buttonRadius,
                        },
                      }}
                    >
                      {actionLabel}
                    </Button>
                  ) : (
                    <CustomDropdownButton
                      handleMainClick={() => handleNext(props.updateRequest)}
                      startIcon={startIcon}
                      disabled={isDisabled}
                      mainDisabled={isSendBlocked}
                      buttonLabel={actionLabel}
                      menuItems={dropdownMenuItems}
                      hasSignAccess={isSignAccess}
                      hasTemplateAccess={
                        props.updateTemplate
                          ? isTemplateUpdateAccess
                          : isTemplateAddAccess
                      }
                      buttonGroupSx={primaryActionSx}
                      arrowButtonSx={primaryActionCaretSx}
                    />
                  );
                // A disabled button explains itself or it just looks broken.
                const disabledReason =
                  isSendGated && sendEmailDisabledTooltip
                    ? sendEmailDisabledTooltip
                    : isSendCreditBlocked
                      ? fillCopy(strings.smsCredits.sendCostShort, {
                          cost: smsCreditCost.total,
                          balance: smsCredits.balance,
                        })
                      : undefined;
                return disabledReason ? (
                  <Tooltip describeChild title={disabledReason} arrow>
                    <span>{actionButton}</span>
                  </Tooltip>
                ) : (
                  actionButton
                );
              })()}
            </Box>
          </Box>
          {/* A send refused for want of credits. The backend's own message
              names what the send needs and what is left, so it is shown
              verbatim — with the route to fix it beside it. */}
          {smsBlockedMessage && (
            <Box sx={smsBlockedStripSx}>
              <Box component="span" sx={smsBlockedTextSx}>
                {smsBlockedMessage}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => history.push(viewpaths.viewMyPlanViewPath)}
                sx={smsBlockedLinkSx}
              >
                {strings.smsCredits.buyLabel}
              </Box>
            </Box>
          )}
          <Box sx={{ width: "100%" }} pt={stepOwnsTopPadding ? 0 : 2}>
            {getStepContent()}
          </Box>
        </Box>
      )}

      <CustomDialogComponent
        openDialog={templateNamePopup}
        handleDialogClose={handleDialogClose}
        handleTemplate={handleTemplate}
        templateName={templateName}
        setTemplateName={setTemplateName}
        templateNameError={templateNameError}
        setTemplateNameError={setTemplateNameError}
        updateTemplate={props.updateTemplate!}
      />

      <AppModal
        open={saveAsDraft}
        onClose={() => setSaveAsDraft(false)}
        title="Save As Draft?"
        size="sm"
        primaryLabel={strings.save}
        onPrimary={handleSaveAsDraft}
        cancelLabel="Cancel"
        onCancel={() => setSaveAsDraft(false)}
      >
        <Typography sx={bodyTextSx}>
          Save your signer request as a draft so you can pick up right where you
          left off anytime.
        </Typography>
      </AppModal>

      <PresendReviewModal
        open={presendReview.isOpen}
        state={presendReview.state}
        fileNames={presendReview.fileNames}
        dismissed={presendReview.dismissed}
        onDismiss={presendReview.dismiss}
        onRestoreDismissed={presendReview.restoreDismissed}
        onClose={closePresendReview}
        onBackToEditor={closePresendReview}
        // The phone layout's editor has no page to jump to, so it gets no page links.
        onShowOnPage={isMobile ? undefined : handlePresendReviewShowOnPage}
        onContinue={handlePresendReviewContinue}
        onRetry={presendReview.retry}
        onSignIn={presendReview.signInAgain}
      />

      <SmsConsentDialog
        open={smsConsentOpen}
        confirming={smsConsentSubmitting}
        onCancel={() => setSmsConsentOpen(false)}
        onConfirm={handleSmsConsentConfirm}
      />

      {/* A bulk run the wallet can't finish: it isn't refused, it stops
          partway, so this is a decision to confirm rather than an error. */}
      <AppModal
        open={bulkCreditWarning}
        onClose={() => setBulkCreditWarning(false)}
        title="Not enough SMS credits for this run"
        size="sm"
        tone="destructive"
        primaryLabel="Send anyway"
        onPrimary={() => handleNext(props.updateRequest)}
        cancelLabel={strings.cancel}
        onCancel={() => setBulkCreditWarning(false)}
      >
        <Typography sx={bodyTextSx}>
          {fillCopy(strings.smsCredits.bulkShort, {
            cost: smsCreditCost.total,
            balance: smsCredits.balance,
          })}
          {smsCreditCost.maxTotal > smsCreditCost.total &&
            ` ${fillCopy(strings.smsCredits.resendHeadroom, {
              resends: strings.SMS_OTP_MAX_RESENDS,
              max: smsCreditCost.maxTotal,
            })}`}
        </Typography>
      </AppModal>

      <GetSaveTemplatePopups
        signRequest={signRequest}
        folderTree={folderTree}
        newTemplate={props.newTemplate}
        errors={errors}
        dispatch={dispatch}
        saveTemplatePopup={saveTemplatePopup}
        setSaveTemplatePopup={setSaveTemplatePopup}
        showFolderTreePopup={showFolderTreePopup}
        setShowFolderTreePopup={setShowFolderTreePopup}
        destFolder={destFolder}
        setDestFolder={setDestFolder}
        selectedFolderData={selectedFolderData}
        setSelectedFolderData={setSelectedFolderData}
        isRequest={isRequest}
      />

      <AppModal
        open={showSignDialog}
        onClose={handleSkipSign}
        title="Sign Document Now?"
        size="sm"
        // Nothing dismisses the shell while the signature is being applied.
        busy={isSigning}
        primaryLabel="Sign"
        onPrimary={handleSign}
        primaryLoading={isSigning}
        primaryDisabled={isSigning}
        cancelLabel="Cancel"
        onCancel={handleSkipSign}
      >
        <Typography sx={bodyTextSx}>
          You are the only signer for this document. Would you like to sign it
          now?
        </Typography>
      </AppModal>
      <CustomConfirmationModal
        confirmationMessage="Would you like to save your signer request as a draft so you can continue where you left off anytime?"
        confirmationTitle="Save as Draft"
        confirmationFooterButtonName="Save as Draft"
        closeButtonName="Stay on Page"
        leaveButtonName="Leave"
        handleButtonCall={async () => {
          await handleDraftBtn(signRequest, setSaveAsDraft, dispatch, true);
          handleCancel();
        }}
        open={openConfirm}
        onLeave={() => {
          setOpenConfirm(false);
          handleCancel();
        }}
        onClose={() => {
          setOpenConfirm(false);
        }}
      />
    </>
  );
};

export default SignRequestComponent;
